import { useCallback, useState } from 'react'

import { useGetUserQuery } from 'common'
import {
  useEnableEmailAgentMutation,
  usePostSchedulingAssistantSettingMutation,
  SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME,
  SCHEDULING_ASSISTANT_ENABLED_SETTING_TYPE,
  SCHEDULING_ASSISTANT_PRIMARY_EVENT_TYPE_SETTING_NAME,
  SCHEDULING_ASSISTANT_PRIMARY_EVENT_TYPE_SETTING_TYPE,
} from 'hooks/scheduling_assistant/api'
import { useGetAssistantEligibleEventTypes } from 'hooks/scheduling_assistant/use_get_assistant_eligible_event_types'

export const useEnableAssistant = ({ userUuid: userUuidProp, eventTypeUuid } = {}) => {
  const {
    data: userData,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetUserQuery(undefined, { skip: Boolean(userUuidProp) })
  const userUuid = userUuidProp ?? userData?.uuid
  const isUserPending = !userUuidProp && isUserLoading

  const {
    eligibleEventTypes,
    isLoading: areEventTypesLoading,
    isFetching: areEventTypesFetching,
    isError: isEventTypesError,
    isEventTypesPending,
  } = useGetAssistantEligibleEventTypes()

  const [enableEmailAgent] = useEnableEmailAgentMutation()
  const [postSchedulingAssistantSetting] = usePostSchedulingAssistantSettingMutation()
  const [isEnabling, setIsEnabling] = useState(false)

  const hasNoEligibleEventTypes = !eventTypeUuid
    && !isEventTypesPending
    && eligibleEventTypes.length === 0

  const enableAssistant = useCallback(async () => {
    if (!userUuid || isEnabling) {
      return { ok: false }
    }

    let resolvedEventTypeUuid = eventTypeUuid
    if (!resolvedEventTypeUuid) {
      resolvedEventTypeUuid = eligibleEventTypes[0]?.uuid
    }

    if (!resolvedEventTypeUuid) {
      return { ok: false, noEligibleEventType: true }
    }

    setIsEnabling(true)
    try {
      await enableEmailAgent().unwrap()
      await postSchedulingAssistantSetting({
        userUuid,
        settingName: SCHEDULING_ASSISTANT_PRIMARY_EVENT_TYPE_SETTING_NAME,
        settingValue: resolvedEventTypeUuid,
        settingType: SCHEDULING_ASSISTANT_PRIMARY_EVENT_TYPE_SETTING_TYPE,
      }).unwrap()
      await postSchedulingAssistantSetting({
        userUuid,
        settingName: SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME,
        settingValue: 'true',
        settingType: SCHEDULING_ASSISTANT_ENABLED_SETTING_TYPE,
      }).unwrap()
      return { ok: true, eventTypeUuid: resolvedEventTypeUuid }
    } catch {
      return { ok: false, error: true }
    } finally {
      setIsEnabling(false)
    }
  }, [
    userUuid,
    isEnabling,
    eventTypeUuid,
    eligibleEventTypes,
    enableEmailAgent,
    postSchedulingAssistantSetting,
  ])

  return {
    enableAssistant,
    hasNoEligibleEventTypes,
    isEnabling,
    userUuid,
    isUserPending,
    isUserLoading,
    isUserError,
    eligibleEventTypes,
    isEventTypesPending,
    isEventTypesError,
    areEventTypesLoading,
    areEventTypesFetching,
  }
}
